/**
 * AbapEnhancementsPanel — SMOD/CMOD enhancement-point inventory (Iter 35).
 *
 * Wires up:
 *   GET /api/workspaces/{id}/sap-ecc/enhancements              (filterable list)
 *   GET /api/workspaces/{id}/sap-ecc/enhancements/summary      (rollup)
 *
 * Surfaces the SMOD (SAP-side enhancement components) + CMOD (customer project
 * bundles) inventory inside the existing SAP ECC › ABAP Code tab as a third
 * sub-tab next to Inventory + Heat map. Critical for S/4HANA transition
 * planning because CMOD-style enhancements are deprecated in 7.5+.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CircleNotchIcon as Spinner,
  PuzzlePieceIcon as PuzzlePiece,
  PackageIcon as Package,
  FolderOpenIcon as Folder,
  CalendarIcon as Calendar,
  CheckCircleIcon as CheckCircle,
  XCircleIcon as XCircle,
  LockKeyIcon as LockKey,
  MagnifyingGlassIcon as Search,
} from "@phosphor-icons/react";
import { getEnhancementsSummary, listEnhancements } from "@/lib/api";

const PAGE = 50;

// Visual badge per exit type — picks up IBM Carbon-ish palette already in use.
const TYPE_COLOR = {
  function: { bg: "#edf5ff", fg: "#0043ce", border: "#a6c8ff" },
  screen:   { bg: "#fff8e1", fg: "#8a6c00", border: "#f1c21b" },
  menu:     { bg: "#defbe6", fg: "#0e6027", border: "#a7f0ba" },
  field:    { bg: "#f6f2ff", fg: "#6929c4", border: "#d4bbff" },
  badi:     { bg: "#fff1f1", fg: "#a2191f", border: "#ffd7d9" },
};

function TypeBadge({ type }) {
  const c = TYPE_COLOR[type] || { bg: "#f4f4f4", fg: "#525252", border: "#c6c6c6" };
  return (
    <span
      className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border whitespace-nowrap"
      style={{ background: c.bg, color: c.fg, borderColor: c.border }}
      data-testid={`enh-type-${type}`}
    >
      {type}
    </span>
  );
}

function StatusPill({ status }) {
  if (!status) {
    return (
      <span className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[var(--ml-border)] text-[var(--ml-text-secondary)]">
        —
      </span>
    );
  }
  const cfg =
    status === "active"
      ? { Icon: CheckCircle, bg: "#defbe6", fg: "#0e6027", border: "#a7f0ba" }
      : status === "inactive"
      ? { Icon: XCircle, bg: "#fff1f1", fg: "#a2191f", border: "#ffd7d9" }
      : { Icon: LockKey, bg: "#f4f4f4", fg: "#525252", border: "#c6c6c6" };
  return (
    <span
      className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1"
      style={{ background: cfg.bg, color: cfg.fg, borderColor: cfg.border }}
      data-testid={`enh-status-${status}`}
    >
      <cfg.Icon size={9} weight="bold" /> {status}
    </span>
  );
}

function SummaryBar({ summary }) {
  if (!summary) return null;
  const t = summary.totals;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="enh-summary">
      <StatCard
        label="Enhancements"
        value={t.all}
        Icon={PuzzlePiece}
        color="#0f62fe"
        testid="enh-total"
      />
      <StatCard
        label="Active"
        value={t.active}
        Icon={CheckCircle}
        color="#0e6027"
        testid="enh-active"
      />
      <StatCard
        label="Inactive"
        value={t.inactive}
        Icon={XCircle}
        color="#a2191f"
        testid="enh-inactive"
      />
      <StatCard
        label="CMOD projects"
        value={t.projects}
        Icon={Folder}
        color="#6929c4"
        testid="enh-projects"
      />
    </div>
  );
}

function StatCard({ label, value, Icon, color, testid }) {
  return (
    <div className="ml-card p-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={16} weight="bold" style={{ color }} />
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          {label}
        </span>
      </div>
      <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function ProjectRollup({ projects }) {
  if (!projects?.length) return null;
  return (
    <div className="ml-card" data-testid="enh-by-project">
      <div className="px-3 py-2 border-b border-[var(--ml-border)] ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
        CMOD projects · {projects.length}
      </div>
      <table className="w-full text-sm">
        <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
          <tr>
            <th className="px-3 py-2 text-left">Project</th>
            <th className="px-3 py-2 text-right">Active</th>
            <th className="px-3 py-2 text-right">Inactive</th>
            <th className="px-3 py-2 text-right">Locked</th>
            <th className="px-3 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr
              key={p.project_name}
              className="border-b last:border-b-0 border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/30"
              data-testid={`enh-project-${p.project_name}`}
            >
              <td className="px-3 py-2 ml-mono font-semibold">{p.project_name}</td>
              <td className="px-3 py-2 ml-mono text-right tabular-nums text-[#0e6027]">{p.active || "—"}</td>
              <td className="px-3 py-2 ml-mono text-right tabular-nums text-[#a2191f]">{p.inactive || "—"}</td>
              <td className="px-3 py-2 ml-mono text-right tabular-nums text-[var(--ml-text-secondary)]">{p.locked || "—"}</td>
              <td className="px-3 py-2 ml-mono text-right tabular-nums font-semibold">{p.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FilterBar({ filters, setFilters, byType, projects, byPackage }) {
  return (
    <div className="ml-card p-3 flex flex-wrap items-center gap-2" data-testid="enh-filters">
      <div className="relative flex-1 min-w-[200px]">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ml-text-secondary)]" />
        <input
          type="text"
          placeholder="Search SMOD / component / include / description"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, offset: 0 }))}
          className="ml-mono text-xs w-full pl-7 pr-2 py-1.5 border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)]"
          data-testid="enh-search"
        />
      </div>
      <select
        value={filters.exitType}
        onChange={(e) => setFilters((f) => ({ ...f, exitType: e.target.value, offset: 0 }))}
        className="ml-mono text-xs px-2 py-1.5 border border-[var(--ml-border)]"
        data-testid="enh-filter-type"
      >
        <option value="">All exit types</option>
        {byType.map((t) => (
          <option key={t.exit_type} value={t.exit_type}>
            {t.exit_type} ({t.count})
          </option>
        ))}
      </select>
      <select
        value={filters.projectName}
        onChange={(e) => setFilters((f) => ({ ...f, projectName: e.target.value, offset: 0 }))}
        className="ml-mono text-xs px-2 py-1.5 border border-[var(--ml-border)]"
        data-testid="enh-filter-project"
      >
        <option value="">All projects</option>
        {projects.map((p) => (
          <option key={p.project_name} value={p.project_name}>
            {p.project_name} ({p.total})
          </option>
        ))}
      </select>
      <select
        value={filters.projectStatus}
        onChange={(e) => setFilters((f) => ({ ...f, projectStatus: e.target.value, offset: 0 }))}
        className="ml-mono text-xs px-2 py-1.5 border border-[var(--ml-border)]"
        data-testid="enh-filter-status"
      >
        <option value="">Any status</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
        <option value="locked">Locked</option>
      </select>
      <select
        value={filters.pkg}
        onChange={(e) => setFilters((f) => ({ ...f, pkg: e.target.value, offset: 0 }))}
        className="ml-mono text-xs px-2 py-1.5 border border-[var(--ml-border)]"
        data-testid="enh-filter-package"
      >
        <option value="">All packages</option>
        {byPackage.map((p) => (
          <option key={p.package} value={p.package}>
            {p.package} ({p.count})
          </option>
        ))}
      </select>
      {(filters.q || filters.exitType || filters.projectName || filters.projectStatus || filters.pkg) && (
        <button
          type="button"
          onClick={() =>
            setFilters({ q: "", exitType: "", projectName: "", projectStatus: "", pkg: "", offset: 0 })
          }
          className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline px-2"
          data-testid="enh-clear-filters"
        >
          Clear ×
        </button>
      )}
    </div>
  );
}

export default function AbapEnhancementsPanel({ workspaceId }) {
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState({ total: 0, rows: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: "",
    exitType: "",
    projectName: "",
    projectStatus: "",
    pkg: "",
    offset: 0,
  });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listEnhancements(workspaceId, {
        limit: PAGE,
        offset: filters.offset,
        q: filters.q || undefined,
        exit_type: filters.exitType || undefined,
        project_name: filters.projectName || undefined,
        project_status: filters.projectStatus || undefined,
        package: filters.pkg || undefined,
      });
      setData(list);
    } finally {
      setLoading(false);
    }
  }, [
    workspaceId,
    filters.offset,
    filters.q,
    filters.exitType,
    filters.projectName,
    filters.projectStatus,
    filters.pkg,
  ]);

  useEffect(() => {
    getEnhancementsSummary(workspaceId).then(setSummary).catch(() => setSummary(null));
  }, [workspaceId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const pageInfo = useMemo(() => {
    const start = filters.offset + 1;
    const end = Math.min(filters.offset + data.rows.length, data.total);
    return { start: data.rows.length ? start : 0, end, total: data.total };
  }, [filters.offset, data.rows.length, data.total]);

  const isEmpty = !loading && summary?.totals?.all === 0;
  if (isEmpty) {
    return (
      <div className="ml-card p-10 text-center" data-testid="enh-empty">
        <PuzzlePiece size={36} weight="duotone" className="text-[var(--ml-text-secondary)] inline mb-3" />
        <div className="text-base font-semibold mb-1">No enhancement-point data yet</div>
        <p className="text-sm text-[var(--ml-text-secondary)] max-w-md mx-auto">
          Drop a CSV / Excel extract of SMOD + CMOD rows on the Import tab.
          A pre-filled template (<code className="ml-mono text-xs">abap_enhancements.csv</code>) is
          on the Import tab too — start there if you want to see the layout.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="enh-panel">
      <SummaryBar summary={summary} />

      <ProjectRollup projects={summary?.by_project || []} />

      <FilterBar
        filters={filters}
        setFilters={setFilters}
        byType={summary?.by_type || []}
        projects={summary?.by_project || []}
        byPackage={summary?.by_package || []}
      />

      {loading && (
        <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="enh-loading">
          <Spinner size={14} className="animate-spin inline mr-2" />Loading enhancements…
        </div>
      )}

      {!loading && (
        <>
          <div className="flex items-center justify-between">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="enh-pageinfo">
              Showing {pageInfo.start}–{pageInfo.end} of {pageInfo.total}
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                disabled={filters.offset === 0}
                onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, f.offset - PAGE) }))}
                data-testid="enh-prev-page"
                className="px-3 py-1.5 text-xs border border-[var(--ml-border)] disabled:opacity-40 hover:bg-[var(--ml-surface)]/40"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={filters.offset + PAGE >= data.total}
                onClick={() => setFilters((f) => ({ ...f, offset: f.offset + PAGE }))}
                data-testid="enh-next-page"
                className="px-3 py-1.5 text-xs border border-[var(--ml-border)] disabled:opacity-40 hover:bg-[var(--ml-surface)]/40"
              >
                Next
              </button>
            </div>
          </div>

          {data.rows.length === 0 ? (
            <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="enh-no-results">
              No enhancements match the current filters.
            </div>
          ) : (
            <div className="ml-card overflow-x-auto" data-testid="enh-table">
              <table className="w-full text-sm">
                <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
                  <tr>
                    <th className="px-3 py-2 text-left">SMOD</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Project</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Component</th>
                    <th className="px-3 py-2 text-left">Include</th>
                    <th className="px-3 py-2 text-left">Package</th>
                    <th className="px-3 py-2 text-left">Last changed</th>
                    <th className="px-3 py-2 text-left">Transport</th>
                    <th className="px-3 py-2 text-left">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`enh-row-${r.enhancement_name}-${r.project_name || "none"}`}
                      className="border-b border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/30"
                    >
                      <td className="px-3 py-2 ml-mono font-semibold">{r.enhancement_name}</td>
                      <td className="px-3 py-2"><TypeBadge type={r.exit_type} /></td>
                      <td className="px-3 py-2 ml-mono">
                        {r.project_name || (
                          <span className="text-[var(--ml-text-secondary)] italic">(standalone)</span>
                        )}
                      </td>
                      <td className="px-3 py-2"><StatusPill status={r.project_status} /></td>
                      <td className="px-3 py-2 ml-mono text-xs">{r.component || "—"}</td>
                      <td className="px-3 py-2 ml-mono text-xs">{r.include_program || "—"}</td>
                      <td className="px-3 py-2 ml-mono text-xs">
                        {r.package ? (
                          <span className="inline-flex items-center gap-1">
                            <Package size={10} weight="bold" /> {r.package}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap">
                        {r.last_changed_at ? (
                          <span className="inline-flex items-center gap-1">
                            <Calendar size={10} weight="bold" />
                            {r.last_changed_at.slice(0, 10)}
                          </span>
                        ) : "—"}
                        {r.last_changed_by && (
                          <span className="block text-[10px] text-[var(--ml-text-secondary)]">{r.last_changed_by}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 ml-mono text-xs">{r.transport_id || "—"}</td>
                      <td className="px-3 py-2 text-xs text-[var(--ml-text-secondary)] max-w-[240px] truncate" title={r.description || ""}>
                        {r.description || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
